import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { RootState } from 'store/ducks';
import { useDispatch, useSelector } from 'react-redux';

import Table from '../../index';
import { CteTableSearch } from './CteTableSearch';

import { getMaxDateInSearchFilter, getMinDateInSearchFilter } from 'utils/Date';
import { alertCustomerNotSelected } from 'utils/Download';
import { DateOutputStr } from 'utils/DateOutput';

import CteServices from 'services/cte.service';
import { CTETableProps, TableFilterState } from './interface';

import { IBatchRequestResponse } from 'models/Batch';
import { CTeState } from 'models/CTEs';
import { useLoadCte } from 'hooks/useLoadCte';
import { ValidateLimitDownloadInLot } from 'utils/ValidateLimitdownloadInLot';

export const CTETable: React.FC<CTETableProps> = (props) => {

  const {
    trackingKey,
    customProps,
    tableSearchClassName,
    currentTabId,
    showFastFilters = true,
    isEditing = false,
    showExportButton = false,
    showDownloadInLotButton = false
  } = props;

  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user);
  const customer = useSelector((state: RootState) => state.customer);

  const cteService = new CteServices();

  const cteSelected = useSelector((state: RootState) => state.openProtocolData?.cte);

  const [tableFilter, setTableFilter] = useState<TableFilterState>({
    documentEbl: "",
    startPeriod: new Date(`${DateOutputStr(getMinDateInSearchFilter()
      .toISOString(), 'yyyy-MM-dd')}T00:00:00.000Z`),
    lastPeriod: new Date(`${DateOutputStr(getMaxDateInSearchFilter()
      .toISOString(), 'yyyy-MM-dd')}T23:59:59.999Z`),
  });

  const [dataCte, setDataCte] = useState<IBatchRequestResponse<CTeState>>();
  const [cteDownloadInLot, setCteDownloadInLot] = useState<any>({
    loading: false
  });

  const [tableOrderBy, setTableOrderBy] = useState('desc');
  const [currentPage, setCurrentPage] = useState(0);

  const pageTotal = props.pageSize || 10;
  const pageTotalDownloadInLot = 100;

  const { customerIds } = useLoadCte();

  useEffect(() => {
    if (!currentTabId) return;
    dispatch({
      type: 'SET_CURRENT_TAB',
      data: currentTabId
    });
  }, [customerIds]);

  useEffect(() => {
    if (isEditing && cteSelected && cteSelected?.length > 0)
      tableFilter.documentEbl = cteSelected[0].documentEbl;
  }, []);

  useEffect(() => {
    getCte();
  }, [currentPage, tableFilter, tableOrderBy, customer, user?.selectedCustomerId]);

  useEffect(() => {
    if (tableFilter && cteDownloadInLot?.data) {
      setCteDownloadInLot({
        ...cteDownloadInLot,
        data: undefined
      });
    }
  }, [tableFilter]);

  const getCte = () => {
    setDataCte(undefined);

    cteService
      .get(currentPage, pageTotal, tableOrderBy, tableFilter, customerIds)
      .then((result: IBatchRequestResponse<CTeState>) => {
        if (result) {
          const { error, data, totalPages, pageSize, total } = result;
          setDataCte({ error, data, totalPages, pageSize, total });
        }
      });
  };

  const getCteForDownloadInLot = async () => {
    if (ValidateLimitDownloadInLot(dataCte?.total, "CTes")) return;
    setCteDownloadInLot({ ...cteDownloadInLot, loading: true });

    await cteService
      .get(
        0,
        pageTotalDownloadInLot,
        tableOrderBy,
        tableFilter,
        customerIds
      )
      .then((result: IBatchRequestResponse<CTeState> | undefined) => {
        if (result?.data) {
          setCteDownloadInLot({
            ...cteDownloadInLot,
            data: result.data,
            loading: false
          });
        }
      });
  };

  const isExportButton = () => {
    if (showExportButton) {
      return {
        show: showExportButton,
        onClick: user?.selectedCustomerId || customer?.id ?
          exportData :
          alertCustomerNotSelected,
      };
    }

    return undefined;
  };

  const exportData = useCallback(async () => {
    await cteService.downloadData(tableOrderBy, tableFilter, customerIds);
  }, [tableFilter, tableOrderBy]);

  const CTETableRender = useMemo(() => (
    <Table
      trackingKey={trackingKey}
      className="mt-3"
      error={dataCte?.error || false}
      onReload={getCte}
      setTableOrder={setTableOrderBy}
      tableOrder={tableOrderBy}
      currentPage={currentPage}
      data={dataCte?.data}
      pageSize={pageTotal}
      onPageChange={setCurrentPage}
      totalPages={dataCte?.totalPages}
      model={props.model || []}
      customProps={customProps || {}}
      totalOrder={dataCte?.total}
      exportButton={isExportButton()}
      downloadInLotButton={showDownloadInLotButton}
      getOrdersForDownloadInLot={getCteForDownloadInLot}
      ordersDownloadInLot={cteDownloadInLot}
    />
  ), [dataCte, user?.selectedCustomerId, customer, tableOrderBy, cteDownloadInLot]);

  return (
    <>
      <CteTableSearch
        className={tableSearchClassName}
        trackingKey={trackingKey}
        placeholder="N° CT-e"
        dateKey="cteData"
        tableOrderBy={tableOrderBy}
        setTableOrderBy={setTableOrderBy}
        setTableFilter={setTableFilter}
        setCurrentPage={setCurrentPage}
        showFastFilters={showFastFilters}
      />
      {CTETableRender}
    </>
  );
};
